import React, { useState, useEffect, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

interface ApiResponse {
  success: boolean;
  data: Course[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface CourseChipsProps {
  onCourseSelect?: (course: Course) => void;
  selectedCourseId?: string;
  showSearch?: boolean;
  maxDisplay?: number;
}

// ── HCI brand tokens (from the logo) ──────────────────────────────────────────
const C = {
  navy: '#14267A', navyMid: '#2238A0', navyDeep: '#0B1650', gold: '#F5B326',
  tint: '#E8EBF8', tintSoft: '#F4F5FA',
  line: '#E2E5F2', lineStrong: '#DADFF0',
  text: '#12183A', textSoft: '#5A6080',
};

const CourseChips: React.FC<CourseChipsProps> = ({
  onCourseSelect,
  selectedCourseId,
  showSearch = true,
  maxDisplay,
}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = railRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, search, loading]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: '1', limit: '100' });
      const response = await apiClient.get<ApiResponse>(`/v1/courses?${params}`);
      if (response.data.success) {
        setCourses(response.data.data);
      } else {
        setError('Failed to load courses');
      }
    } catch (err: any) {
      console.error('Failed to fetch courses:', err);
      setError(err.response?.data?.error || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const q = search.toLowerCase();
  const filteredCourses = courses.filter(
    (course) => course.name.toLowerCase().includes(q) || course.title.toLowerCase().includes(q)
  );
  const displayedCourses = maxDisplay ? filteredCourses.slice(0, maxDisplay) : filteredCourses;

  const scrollRail = (dir: 1 | -1) => {
    railRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  const wrap: React.CSSProperties = { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: C.text };

  if (loading) {
    return (
      <div style={{ ...wrap, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '28px', background: C.tintSoft, borderRadius: '16px' }}>
        <ProgressSpinner style={{ width: '32px', height: '32px' }} strokeWidth="3" />
      </div>
    );
  }

  if (error) {
    return <Message severity="error" text={error} style={{ fontSize: '0.8rem', fontWeight: 500 }} />;
  }

  const edgeBtn = (side: 'left' | 'right', visible: boolean): React.ReactNode => (
    <button
      onClick={() => scrollRail(side === 'left' ? -1 : 1)}
      aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
      tabIndex={visible ? 0 : -1}
      style={{
        position: 'absolute', top: '50%', [side]: '-8px', transform: 'translateY(-50%)', zIndex: 2,
        width: '28px', height: '28px', borderRadius: '50%',
        border: `1px solid ${C.lineStrong}`, background: 'white', color: C.navy, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(20,38,122,0.12)',
        opacity: visible ? 1 : 0, pointerEvents: visible ? 'auto' : 'none', transition: 'opacity 0.2s ease',
      }}
    >
      <i className={`pi ${side === 'left' ? 'pi-chevron-left' : 'pi-chevron-right'}`} style={{ fontSize: '0.7rem' }}></i>
    </button>
  );

  return (
    <div style={wrap}>
      {showSearch && (
        <div style={{ position: 'relative', width: '320px', maxWidth: '100%', marginBottom: '12px' }}>
          <i className="pi pi-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: C.textSoft, fontSize: '0.85rem' }}></i>
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search courses..."
            style={{ width: '100%', paddingLeft: '40px', height: '40px', border: `1px solid ${C.lineStrong}`, borderRadius: '12px', fontSize: '0.8rem', background: 'white' }}
          />
        </div>
      )}

      {displayedCourses.length === 0 ? (
        <div style={{ padding: '36px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: `1px dashed ${C.lineStrong}` }}>
          <i className="pi pi-book" style={{ fontSize: '1.8rem', color: '#C6CDE8', marginBottom: '8px' }}></i>
          <p style={{ margin: 0, color: C.textSoft, fontSize: '0.78rem' }}>
            {search ? 'No courses match your search' : 'No courses available'}
          </p>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
        {edgeBtn('left', canLeft)}
        {edgeBtn('right', canRight)}
        <div
          ref={railRef}
          className="course-rail"
          onScroll={updateArrows}
          style={{
            display: 'flex', gap: '10px', overflowX: 'auto', scrollSnapType: 'x proximity',
            padding: '4px 2px 6px', scrollbarWidth: 'none', msOverflowStyle: 'none',
          }}
        >
          <style>{`.course-rail::-webkit-scrollbar { display: none; } @keyframes courseDotPulse { 0%,100% { opacity: 1; box-shadow: 0 0 0 0 var(--dot-glow); } 50% { opacity: 0.45; box-shadow: 0 0 0 5px transparent; } }`}</style>
          {displayedCourses.map((course) => {
            const selected = selectedCourseId === course.course_id;
            const hov = hoveredId === course.course_id && !selected;
            const isActive = course.status === 'active';
            return (
              <div
                key={course.course_id}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => onCourseSelect?.(course)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCourseSelect?.(course); } }}
                onMouseEnter={() => setHoveredId(course.course_id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  scrollSnapAlign: 'start', flex: '0 0 auto', width: '230px',
                  position: 'relative', overflow: 'hidden',
                  padding: '12px 14px', borderRadius: '16px',
                  background: selected ? `linear-gradient(135deg, ${C.navyMid}, ${C.navy})` : hov ? '#EEF3FF' : 'white',
                  border: `1px solid ${selected ? C.navy : hov ? '#C6D4FF' : C.line}`,
                  boxShadow: selected ? '0 3px 10px rgba(20,38,122,0.16)' : hov ? '0 2px 6px rgba(20,38,122,0.06)' : 'none',
                  transform: hov ? 'translateY(-2px)' : 'translateY(0)',
                  cursor: onCourseSelect ? 'pointer' : 'default',
                  transition: 'all 0.25s ease', outlineOffset: '2px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
                    background: selected ? C.gold : C.tint, color: selected ? C.navyDeep : C.navy,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.85rem', fontWeight: 800,
                  }}>
                    {course.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, letterSpacing: '-0.2px', color: selected ? 'white' : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {course.name}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: selected ? 'rgba(255,255,255,0.75)' : C.textSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {course.title}
                    </div>
                  </div>
                  <span
                    title={course.status}
                    aria-label={`Status: ${course.status}`}
                    style={{
                      alignSelf: 'flex-start', marginTop: '2px', flexShrink: 0,
                      width: '9px', height: '9px', borderRadius: '50%',
                      background: isActive ? '#22C55E' : '#EF4444',
                      ['--dot-glow' as any]: isActive ? 'rgba(34,197,94,0.55)' : 'rgba(239,68,68,0.55)',
                      animation: 'courseDotPulse 1.4s ease-in-out infinite',
                    }}
                  />
                </div>

              </div>
            );
          })}
        </div>
        </div>
      )}

      {maxDisplay && filteredCourses.length > maxDisplay && (
        <div style={{ marginTop: '6px', textAlign: 'center', fontSize: '0.72rem', color: C.textSoft }}>
          Showing {maxDisplay} of {filteredCourses.length} courses
        </div>
      )}
    </div>
  );
};

export default CourseChips;